import {
  Button,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import Decimal from "decimal.js";
import { STATUS_TO_BADGE } from "features/credit-facility/settings/CreditFacilitySettingsDetail";
import {
  useCreditFacilityBorrowingBaseGroupList,
  useCreditFacilityBorrowingBaseGroupUpsert,
} from "gen/endpoints/credit-facility-borrowing-base-group/credit-facility-borrowing-base-group";
import { CreditFacilityBorrowingBaseGroupReadData } from "gen/model/creditFacilityBorrowingBaseGroupReadData";
import { CreditFacilityBorrowingBaseGroupUpsertDataRequest } from "gen/model/creditFacilityBorrowingBaseGroupUpsertDataRequest";
import { CreditFacilityBorrowingBaseGroupWithCountData } from "gen/model/creditFacilityBorrowingBaseGroupWithCountData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { VersionStatusEnum } from "gen/model/versionStatusEnum";
import { creditFacilityBorrowingBaseGroupUpsertBody } from "gen/schemas/credit-facility-borrowing-base-group/credit-facility-borrowing-base-group";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  CardSection,
  CardSectionBody,
  CardSectionGroup,
} from "shared/components/card/CardSection";
import Form from "shared/components/form/Form";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { RouteLink } from "shared/components/link/RouteLink";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { nowAsIsoDate } from "shared/utils/DateUtils";
import { formatPercentage } from "shared/utils/formattingUtils";
import { useBoolean } from "usehooks-ts";

export const CreditFacilitySettingsBorrowingBase = () => {
  const { t } = useTranslation(["creditFacility", "common"]);

  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();

  const { data: borrowingGroupData } = useCreditFacilityBorrowingBaseGroupList(
    fundFamilyId,
    creditFacilityId,
  );
  const {
    isOpen: isModalOpen,
    onOpen: openEditDateModal,
    onClose: closeEditDateModal,
  } = useDisclosure();

  return (
    <PageTabBody>
      {borrowingGroupData?.map((borrowingBaseGroup, index) => (
        <BorrowingBaseCard
          key={index}
          borrowingBaseGroup={borrowingBaseGroup}
        />
      ))}
      <ExcludedInvestors />
      <Restricted permission={PermissionTypeEnum.config}>
        <Button colorScheme="secondary" size="sm" onClick={openEditDateModal}>
          {t("creditFacility:borrowingBase.update.addGroup")}
        </Button>
      </Restricted>
      {isModalOpen && (
        <AddBorrowingBaseModal onCloseComplete={closeEditDateModal} />
      )}
    </PageTabBody>
  );
};

const BorrowingBaseCard: React.FC<{
  borrowingBaseGroup: CreditFacilityBorrowingBaseGroupWithCountData;
}> = ({ borrowingBaseGroup }) => {
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();
  const { t } = useTranslation(["creditFacility", "common"]);
  const settingsRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .setup({})
    .creditFacilities({});

  return (
    <HStack spacing="0" mb="16px">
      <Card variant="stat" w="100%" pt="16px" gap="0px" flexDirection="row">
        <VStack alignItems="stretch" flex={1}>
          <CardHeader>
            <HStack>
              <Text fontSize="18px">{borrowingBaseGroup.name}</Text>
              {STATUS_TO_BADGE[borrowingBaseGroup.version_status](
                borrowingBaseGroup.version_status == VersionStatusEnum.scheduled
                  ? borrowingBaseGroup.version_start_date
                  : undefined,
              )}
            </HStack>
          </CardHeader>
          <CardBody>
            <CardSectionGroup flexWrap="wrap">
              <CardSection flexGrow="1">
                <CardSectionBody alignItems="stretch">
                  <StatGroup alignItems="center" paddingTop="0px !important">
                    <Stat>
                      <StatNumber width="360px">
                        <Text>
                          {formatPercentage(
                            new Decimal(borrowingBaseGroup.rate).times(100),
                          )}
                        </Text>
                      </StatNumber>
                      <StatLabel>
                        {t(
                          "creditFacility:borrowingBase.update.unfundedCommitmentAvailableForBorrowing",
                        )}
                      </StatLabel>
                    </Stat>
                    <Stat>
                      <StatNumber>
                        <Text>{borrowingBaseGroup.investor_entity_count}</Text>
                      </StatNumber>
                      <StatLabel>
                        {t(
                          "creditFacility:borrowingBase.update.investorEntities",
                        )}
                      </StatLabel>
                    </Stat>
                    {!!borrowingBaseGroup.individual_concentration_limit && (
                      <Stat>
                        <StatNumber>
                          <Text>
                            {formatPercentage(
                              new Decimal(
                                borrowingBaseGroup.individual_concentration_limit,
                              ).times(100),
                            )}
                          </Text>
                        </StatNumber>
                        <StatLabel>
                          {t(
                            "creditFacility:borrowingBase.concentrationLimits.individualConcentrationLimit",
                          )}
                        </StatLabel>
                      </Stat>
                    )}
                    {!!borrowingBaseGroup.aggregate_concentration_limit && (
                      <Stat>
                        <StatNumber>
                          <Text>
                            {formatPercentage(
                              new Decimal(
                                borrowingBaseGroup.aggregate_concentration_limit,
                              ).times(100),
                            )}
                          </Text>
                        </StatNumber>
                        <StatLabel>
                          {t(
                            "creditFacility:borrowingBase.concentrationLimits.aggregateConcentrationLimit",
                          )}
                        </StatLabel>
                      </Stat>
                    )}
                  </StatGroup>
                </CardSectionBody>
              </CardSection>
            </CardSectionGroup>
          </CardBody>
        </VStack>
        <Restricted permission={PermissionTypeEnum.config}>
          <HStack gap="16px" pr="24px">
            <RouteLink
              to={
                settingsRoute.creditFacilitySettingsBorrowingBaseEdit({
                  creditFacilityId,
                  borrowingBaseGroupId: borrowingBaseGroup.id,
                }).$
              }
            >
              <Text fontSize="16px">
                {t("creditFacility:borrowingBase.update.edit")}
              </Text>
            </RouteLink>
          </HStack>
        </Restricted>
      </Card>
    </HStack>
  );
};

const ExcludedInvestors = () => {
  const { t } = useTranslation(["creditFacility", "common"]);

  return (
    <HStack spacing="0" mb="16px">
      <Card variant="stat" w="100%" pt="16px" gap="0px" flexDirection="row">
        <VStack alignItems="stretch" flex={1}>
          <CardHeader>
            <VStack alignItems="flex-start" gap="2px">
              <Text fontSize="18px">
                {t("creditFacility:borrowingBase.update.excludedInvestors")}
              </Text>
              <Text fontSize="12px">
                {t("creditFacility:borrowingBase.update.excludedInvestorsInfo")}
              </Text>
            </VStack>
          </CardHeader>
          <CardBody>
            <CardSectionGroup flexWrap="wrap">
              <CardSection flexGrow="1">
                <CardSectionBody alignItems="stretch">
                  <StatGroup alignItems="center" paddingTop="0px !important">
                    <Stat>
                      <StatNumber width="360px">
                        <Text>{formatPercentage("0")}</Text>
                      </StatNumber>
                      <StatLabel>
                        {t(
                          "creditFacility:borrowingBase.update.unfundedCommitmentAvailableForBorrowing",
                        )}
                      </StatLabel>
                    </Stat>
                  </StatGroup>
                </CardSectionBody>
              </CardSection>
            </CardSectionGroup>
          </CardBody>
        </VStack>
      </Card>
    </HStack>
  );
};

interface AddBorrowingBaseGroupModalProps {
  onCloseComplete?: () => void;
}

const AddBorrowingBaseModal: React.FC<AddBorrowingBaseGroupModalProps> = ({
  onCloseComplete,
}) => {
  const { t } = useTranslation(["creditFacility", "common"]);
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();
  const { value: isOpen, setFalse: close } = useBoolean(true);
  const navigate = useNavigate();
  const cancelRef = useRef(null);
  const { mutateAsync: createBorrowingBase } =
    useCreditFacilityBorrowingBaseGroupUpsert();

  const settingsRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .setup({})
    .creditFacilities({});

  const form = useForm<CreditFacilityBorrowingBaseGroupUpsertDataRequest>({
    defaultValues: {
      version_date: nowAsIsoDate(),
      name: "",
      rate: "0",
      individual_concentration_limit: null,
      aggregate_concentration_limit: null,
      credit_facility_id: creditFacilityId,
    },
    resolver: zodResolver(creditFacilityBorrowingBaseGroupUpsertBody),
  });
  const onSubmit = async (
    data: CreditFacilityBorrowingBaseGroupUpsertDataRequest,
  ) =>
    await createBorrowingBase({
      fundFamilyId,
      creditFacilityId,
      data,
    });

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      initialFocusRef={cancelRef}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("creditFacility:settings.newBorrowingBaseModal.title")}
        </ModalHeader>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={(group: CreditFacilityBorrowingBaseGroupReadData) =>
            navigate(
              settingsRoute.creditFacilitySettingsBorrowingBaseEdit({
                creditFacilityId,
                borrowingBaseGroupId: group.id,
              }).$,
            )
          }
        >
          <ModalBody>
            <FormDateInput
              name="version_date"
              title={t(
                "creditFacility:settings.newBorrowingBaseModal.inceptionDate",
              )}
            />
            <FormTextInput
              showMessages={true}
              name="name"
              title={t(
                "creditFacility:borrowingBase.update.advanceRateGroupName",
              )}
            />
            <FormNumberInput
              showMessages={true}
              name="rate"
              valueType="percent"
              title={t(
                "creditFacility:borrowingBase.update.unfundedCommitmentAvailableForBorrowing",
              )}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={close} ref={cancelRef} />}
              confirm={
                <FormSubmitButton>
                  {t("creditFacility:settings.newBorrowingBaseModal.submit")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
